import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { SiteHeader } from "./SiteHeader";
import { SiteFooter } from "./SiteFooter";
import { QuoteCTA } from "./QuoteCTA";
import { LetsTalkForm } from "./LetsTalkForm";
import { AwningExtras, AWNING_EXTRA_FIELDS_SUMMARY } from "./AwningExtras";
import { RelatedLinks } from "./RelatedLinks";
import { ServiceInternalLinks } from "./ServiceInternalLinks";
import { ServiceCityLinks } from "./ServiceCityLinks";
import { SERVICE_CITY_SERVICES, type ServiceCityService } from "@/data/serviceCityMatrix";
import { Lightbox } from "./Lightbox";
import { Breadcrumbs, type BreadcrumbItem } from "./Breadcrumbs";
import { PageOutline, type PageOutlineItem } from "./PageOutline";
import { Check, MapPin, ArrowRight, Maximize2, Phone, Quote, Star } from "lucide-react";
import { linkifyBrands } from "@/lib/linkify-brands";
import { getServiceHeadings } from "@/lib/serviceCityHeadings";
import { type HeroImageSource, isPicture } from "@/lib/picture";
import { LazyImage } from "@/components/LazyImage";

export interface CityCallout {
  to: string;
  city: string;
  anchorText: string;
  blurb: string;
}

export interface GallerySection {
  eyebrow?: string;
  title: string;
  intro?: React.ReactNode;
  /** Optional H3-level subheadings rendered above the gallery grid for SEO */
  subsections?: { heading: string; body: React.ReactNode; id?: string }[];
  /** Optional list of related internal links rendered below the gallery */
  relatedLinks?: { to: string; anchor: string; blurb?: string }[];
  images: { src: string; alt: string; label?: string; caption?: string; details?: string[]; title?: string; category?: string; linkHref?: string; linkLabel?: string }[];
  /** Optional category filter tabs shown above the gallery. Order is preserved. */
  categories?: string[];
  columns?: 2 | 3 | 4;
  cta?: {
    headline: string;
    subhead?: string;
    buttonLabel: string;
    href: string;
    phoneNumber?: string;
    secondaryButtonLabel?: string;
    secondaryHref?: string;
  };
}

export interface ServicePageProps {
  eyebrow: string;
  title: string;
  intro: string;
  /** Static hero image. Required unless heroVideo is provided. */
  heroImage?: HeroImageSource;
  features: string[];
  body: { heading: string; text: string; bullets?: string[]; cta?: { label: string; href: string }; image?: { src: string; alt: string; caption?: string }; imageOnly?: boolean }[];
  applications: string[];
  /** Optional override for the applications section eyebrow (default: "APPLICATIONS") */
  applicationsEyebrow?: string;
  /** Optional override for the applications section heading (default: "Where We Install") */
  applicationsHeading?: string;
  faqs?: { q: string; a: string; aJsx?: React.ReactNode }[];
  /** Path of this service page, e.g. "/awnings" — enables related-links section */
  currentPath?: string;
  /** Short service name used in related-links headings, e.g. "Awnings" */
  serviceName?: string;
  /** Optional city-specific callouts rendered before QuoteCTA */
  cityCallouts?: CityCallout[];
  /** Optional extra image gallery section (e.g. School/Athletic field covers) */
  gallerySection?: GallerySection;
  /** When "beforeLetsTalk", the gallery renders immediately above the Let's Talk form (default placement is later in the page) */
  gallerySectionPlacement?: "default" | "beforeLetsTalk";
  /** Optional "Project Highlights" cards with title, blurb, and 3–5 spec bullets */
  projectHighlights?: {
    eyebrow?: string;
    title: string;
    intro?: string;
    items: { title: string; description: string; specs: string[] }[];
  };
  /** Optional testimonials section, typically rendered between the CTA and project highlights */
  testimonials?: {
    eyebrow?: string;
    title?: string;
    items: { quote: string; author: string; role?: string; location?: string; rating?: number }[];
  };
  /** Optional CTA banner rendered immediately below the hero */
  heroCta?: {
    eyebrow?: string;
    headline: string;
    subhead?: string;
    primaryHref: string;
    primaryLabel: string;
    phoneNumber?: string; // e.g. "+1 (830) 390-9588" — adds tel: button
    image?: { src: HeroImageSource; alt: string; caption?: string };
  };
  /** Optional extra content rendered just before the final QuoteCTA */
  children?: React.ReactNode;
  /** Optional extra content rendered AFTER the final QuoteCTA, just above the footer */
  postCta?: React.ReactNode;
  /** Optional extra content rendered immediately after the hero (above the WHY US / body section) */
  preContent?: React.ReactNode;
  /** Optional extra content rendered immediately AFTER the WHY US section */
  afterWhyUs?: React.ReactNode;
  /** Optional looping video background for the hero section. When provided it replaces the static hero image. */
  heroVideo?: { src: string; poster?: string };
  /** When true, hides the default <LetsTalkForm /> section (use when the page provides its own forms) */
  hideLetsTalkForm?: boolean;
  /** Optional image shown next to the default Let's Talk form */
  letsTalkImageSrc?: string;
  /** Alt text for the Let's Talk form image */
  letsTalkImageAlt?: string;
  /** When true, the default Let's Talk form includes the awning size / series / fabric option fields */
  letsTalkAwningFields?: boolean;
  /** Series values pre-checked in the awning fields (must match the option values) */
  letsTalkAwningSeries?: string[];
  /** When true, hides the default <RelatedLinks /> block */
  hideRelatedLinks?: boolean;
  /** When true, hides the auto-rendered ServiceInternalLinks + ServiceCityLinks blocks */
  hideAutoInternalLinks?: boolean;
  /** When true, hides the Partners & Suppliers block in the footer */
  hideFooterPartners?: boolean;
  /** Optional SEO breadcrumb trail (Home is auto-prepended). Last item is current page. */
  breadcrumbs?: BreadcrumbItem[];
  /** Optional keyword-mapped TOC rendered above the WHY US section. */
  keywordOutline?: PageOutlineItem[];
  /** Optional override heading for the keyword outline. */
  keywordOutlineHeading?: string;
  /** Optional image rendered beside the keyword outline list. */
  keywordOutlineImageSrc?: string;
  keywordOutlineImageAlt?: string;
  /** Optional city for keyword H2 interpolation (defaults to "Texas"). */
  city?: string;
  /** When "afterHero", WHY US renders right under the hero (before LetsTalkForm). Default renders after LetsTalkForm. */
  whyUsPlacement?: "default" | "afterHero";
  /** Render the body section as a scannable accordion instead of a card grid. */
  bodyLayout?: "cards" | "accordion";
  /** When true, the FAQ section does not emit its own FAQPage JSON-LD (the route emits a single merged FAQPage in head()). */
  hideFaqJsonLd?: boolean;
  /** Optional media node rendered inside the FAQ section, floated so text wraps. */
  faqMedia?: React.ReactNode;
  /** Optional Tailwind classes for the hero <img loading="lazy" decoding="async"> (merged with defaults). */
  heroImageClassName?: string;
  /** Optional Tailwind classes for the hero gradient overlay (replaces default). */
  heroOverlayClassName?: string;
  /** Optional Tailwind classes replacing the hero content padding container (controls hero height). */
  heroContentClassName?: string;
}

export function ServicePage({ eyebrow, title, intro, heroImage, features, body, applications, applicationsEyebrow, applicationsHeading, faqs, currentPath, serviceName, cityCallouts, gallerySection, gallerySectionPlacement = "default", heroCta, projectHighlights, testimonials, children, postCta, preContent, afterWhyUs, heroVideo, hideLetsTalkForm, letsTalkImageSrc, letsTalkImageAlt, letsTalkAwningFields, letsTalkAwningSeries, hideRelatedLinks, hideAutoInternalLinks, hideFooterPartners, breadcrumbs, keywordOutline, keywordOutlineHeading, keywordOutlineImageSrc, keywordOutlineImageAlt, city, whyUsPlacement = "default", bodyLayout = "cards", faqMedia, hideFaqJsonLd, heroImageClassName, heroOverlayClassName, heroContentClassName }: ServicePageProps) {
  const autoHeadings = getServiceHeadings(currentPath, city);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const filteredImages = gallerySection
    ? activeCategory === "All"
      ? gallerySection.images
      : gallerySection.images.filter((img) => img.category === activeCategory)
    : [];

  const gallerySectionNode = gallerySection ? (
    <section id="products" className="bg-white py-20 scroll-mt-24">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        {gallerySection.eyebrow && (
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ {gallerySection.eyebrow}</div>
        )}
        <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">{gallerySection.title || autoHeadings?.products}</h2>
        {gallerySection.intro && (
          <div className="mt-4 max-w-3xl text-lg text-navy-deep/80">{gallerySection.intro}</div>
        )}
        {gallerySection.subsections && gallerySection.subsections.length > 0 && (
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {gallerySection.subsections.map((s) => (
              <div key={s.heading} id={s.id} className="scroll-mt-24 border-l-4 border-red bg-cream/50 p-5">
                <h3 className="font-display text-xl font-bold text-navy-deep">{s.heading}</h3>
                <div className="mt-2 text-sm text-navy-deep/80">{s.body}</div>
              </div>
            ))}
          </div>
        )}
        {gallerySection.categories && gallerySection.categories.length > 0 && (
          <div
            role="group"
            aria-label="Filter gallery by awning type"
            className="mt-8 flex flex-wrap gap-2"
          >
            {["All", ...gallerySection.categories].map((cat) => {
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActiveCategory(cat)}
                  className={`border-2 px-4 py-2 font-display text-xs uppercase tracking-wider transition ${
                    isActive
                      ? "border-red bg-red text-white"
                      : "border-navy-deep/20 bg-white text-navy-deep hover:border-red hover:text-red"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        )}
        <ul
          role="list"
          aria-label={`${gallerySection.title} photo gallery`}
          className={`mt-10 grid list-none gap-5 p-0 sm:grid-cols-2 sm:gap-6 lg:gap-8 ${gallerySection.columns === 2 ? "lg:grid-cols-2" : gallerySection.columns === 3 ? "lg:grid-cols-3" : "md:grid-cols-3 lg:grid-cols-4"}`}
        >
          {filteredImages.map((img, i) => {
            const captionId = `gallery-cap-${i}-${img.src.replace(/\W+/g, "-")}`;
            const accessibleLabel = img.label
              ? `${img.label}. ${img.alt}. Open larger view.`
              : `${img.alt}. Open larger view.`;
            const lightboxIdx = gallerySection.images.indexOf(img);
            return (
              <li key={img.src}>
                <figure className="group relative overflow-hidden border-2 border-navy-deep/10 bg-cream shadow-card">
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(lightboxIdx)}
                    aria-label={accessibleLabel}
                    aria-haspopup="dialog"
                    aria-describedby={img.label ? captionId : undefined}
                    className="block w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-red focus-visible:ring-offset-2"
                  >
                    <LazyImage
                      src={img.src}
                      alt={img.alt}
                      width={1200}
                      height={900}
                      fetchPriority="low"
                      sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                      wrapperClassName="aspect-[4/3] w-full"
                      className="aspect-[4/3] w-full object-cover group-hover:scale-105"
                    />
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 flex items-center justify-center bg-navy-deep/0 opacity-0 transition group-hover:bg-navy-deep/40 group-hover:opacity-100"
                    >
                      <span className="flex items-center gap-2 bg-white px-4 py-2 font-display text-xs uppercase tracking-wider text-navy-deep">
                        <Maximize2 className="h-4 w-4" /> View
                      </span>
                    </span>
                  </button>
                  {(img.label || img.caption || img.linkHref) && (
                    <figcaption id={captionId} className="bg-navy-deep text-white">
                      {img.label && (
                        <div className="px-4 py-2 text-center font-display text-xs uppercase tracking-[0.3em]">
                          {img.label}
                        </div>
                      )}
                      {img.caption && (
                        <div className="border-t border-white/10 bg-cream px-4 py-3 text-center text-sm text-navy-deep/80">
                          {img.caption}
                        </div>
                      )}
                      {img.linkHref && (
                        <div className="border-t border-white/10 bg-cream px-4 pb-4 pt-1 text-center">
                          <a
                            href={img.linkHref}
                            className="inline-flex items-center gap-1.5 font-display text-xs font-bold uppercase tracking-wider text-red transition hover:text-red-deep"
                          >
                            {img.linkLabel ?? "See details"} <ArrowRight className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      )}
                    </figcaption>
                  )}
                </figure>
              </li>
            );
          })}
        </ul>
        {gallerySection.relatedLinks && gallerySection.relatedLinks.length > 0 && (
          <nav aria-label="Related services" className="mt-12 border-t border-navy-deep/10 pt-8">
            <h3 className="font-display text-xl font-bold text-navy-deep">Related Custom Cover & Pad Services</h3>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {gallerySection.relatedLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="group flex items-start gap-2 border border-navy-deep/15 bg-white p-4 transition hover:border-red hover:bg-cream"
                  >
                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-red transition group-hover:translate-x-1" />
                    <span>
                      <span className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep">{l.anchor}</span>
                      {l.blurb && <span className="mt-1 block text-sm text-navy-deep/70">{l.blurb}</span>}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
      <Lightbox
        images={gallerySection.images}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />
    </section>
  ) : null;

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        {breadcrumbs && breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />}
        <section id="overview" className="relative bg-navy-deep text-white scroll-mt-24">
          <div className="absolute inset-0 overflow-hidden">
            {heroVideo ? (
              <video
                src={heroVideo.src}
                poster={heroVideo.poster}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label={title}
                className={`h-full w-full ${/object-(cover|contain|fill|none)/.test(heroImageClassName ?? "") ? "" : "object-cover "}aspect-[4/5] sm:aspect-[16/10] lg:aspect-[21/9] ${heroImageClassName ?? "opacity-40"}`}
              />
            ) : heroImage ? (
              <picture className="block h-full w-full">
                {isPicture(heroImage) &&
                  Object.entries(heroImage.sources).map(([type, srcSet]) => (
                    <source
                      key={type}
                      type={`image/${type}`}
                      srcSet={srcSet}
                      sizes="100vw"
                    />
                  ))}
                <img
                  src={isPicture(heroImage) ? heroImage.img.src : heroImage}
                  alt={title}
                  width={isPicture(heroImage) ? heroImage.img.w : 1920}
                  height={isPicture(heroImage) ? heroImage.img.h : 1080}
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  className={`h-full w-full ${/object-(cover|contain|fill|none)/.test(heroImageClassName ?? "") ? "" : "object-cover "}aspect-[4/5] sm:aspect-[16/10] lg:aspect-[21/9] ${heroImageClassName ?? "opacity-40"}`}
                />
              </picture>
            ) : null}
            <div className={`absolute inset-0 ${heroOverlayClassName ?? "bg-gradient-to-r from-navy-deep via-navy-deep/80 to-transparent"}`} />
          </div>
          <div className={heroContentClassName ?? "relative mx-auto max-w-7xl px-4 py-12 sm:py-16 md:py-24 lg:px-8 lg:py-32"}>
            <div className="inline-block bg-red px-3 py-1 font-display text-xs tracking-[0.3em] text-white">{eyebrow}</div>
            <h1 className="mt-3 max-w-3xl font-display text-3xl font-bold leading-tight sm:mt-4 sm:text-5xl md:text-6xl lg:text-7xl">{title}</h1>
            <p className="mt-4 max-w-2xl text-base text-white/85 sm:mt-6 sm:text-lg">{linkifyBrands(intro)}</p>
          </div>
        </section>

        {heroCta && (
          <section className="bg-red text-white">
            <div className="mx-auto max-w-7xl px-4 py-10 lg:px-8 lg:py-12">
              {heroCta.image ? (
                <div className="overflow-hidden">
                  <figure className="mx-auto mb-4 w-40 overflow-hidden rounded-sm border-2 border-white/40 shadow-lg sm:float-right sm:mx-0 sm:ml-5 sm:mb-2 sm:w-[28%] md:w-[24%] lg:w-[22%]">
                    <picture className="block">
                      {isPicture(heroCta.image.src) &&
                        Object.entries(heroCta.image.src.sources).map(([type, srcSet]) => (
                          <source
                            key={type}
                            type={`image/${type}`}
                            srcSet={srcSet}
                            sizes="(min-width: 1024px) 22vw, (min-width: 640px) 28vw, 160px"
                          />
                        ))}
                      <img
                        src={isPicture(heroCta.image.src) ? heroCta.image.src.img.src : heroCta.image.src}
                        alt={heroCta.image.alt}
                        width={isPicture(heroCta.image.src) ? heroCta.image.src.img.w : undefined}
                        height={isPicture(heroCta.image.src) ? heroCta.image.src.img.h : undefined}
                        loading="lazy"
                        decoding="async"
                        fetchPriority="low"
                        sizes="(min-width: 1024px) 22vw, (min-width: 640px) 28vw, 160px"
                        className="block h-auto w-full object-cover"
                      />
                    </picture>
                    {heroCta.image.caption && (
                      <figcaption className="bg-navy-deep px-3 py-1.5 font-display text-[10px] uppercase tracking-[0.2em] text-white">
                        {heroCta.image.caption}
                      </figcaption>
                    )}
                  </figure>
                  {heroCta.eyebrow && (
                    <div className="font-display text-xs uppercase tracking-[0.3em] text-white/80">★ {heroCta.eyebrow}</div>
                  )}
                  <h2 className="mt-2 font-display text-3xl font-bold leading-tight md:text-4xl">{heroCta.headline}</h2>
                  {heroCta.subhead && (
                    <p className="mt-3 text-base text-white/90 md:text-lg">{heroCta.subhead}</p>
                  )}
                  <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    <a
                      href={heroCta.primaryHref}
                      className="inline-flex items-center justify-center gap-2 bg-white px-7 py-4 font-display text-base font-extrabold uppercase tracking-wider text-red shadow-xl ring-2 ring-white/40 transition hover:bg-cream active:scale-[0.98] md:text-lg"
                    >
                      {heroCta.primaryLabel} <ArrowRight className="h-5 w-5" />
                    </a>
                    {heroCta.phoneNumber && (
                      <a
                        href={`tel:${heroCta.phoneNumber.replace(/\D/g, "")}`}
                        className="inline-flex items-center justify-center gap-2 border-2 border-white/80 px-6 py-4 font-display text-base font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-red md:text-lg"
                      >
                        <Phone className="h-5 w-5" /> {heroCta.phoneNumber}
                      </a>
                    )}
                  </div>
                  <div className="clear-both" />
                </div>
              ) : (
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                  <div className="max-w-2xl">
                    {heroCta.eyebrow && (
                      <div className="font-display text-xs uppercase tracking-[0.3em] text-white/80">★ {heroCta.eyebrow}</div>
                    )}
                    <h2 className="mt-2 font-display text-3xl font-bold leading-tight md:text-4xl">{heroCta.headline}</h2>
                    {heroCta.subhead && (
                      <p className="mt-3 text-base text-white/90 md:text-lg">{heroCta.subhead}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap lg:shrink-0">
                    <a
                      href={heroCta.primaryHref}
                      className="inline-flex items-center justify-center gap-2 bg-white px-7 py-4 font-display text-base font-extrabold uppercase tracking-wider text-red shadow-xl ring-2 ring-white/40 transition hover:bg-cream active:scale-[0.98] md:text-lg"
                    >
                      {heroCta.primaryLabel} <ArrowRight className="h-5 w-5" />
                    </a>
                    {heroCta.phoneNumber && (
                      <a
                        href={`tel:${heroCta.phoneNumber.replace(/\D/g, "")}`}
                        className="inline-flex items-center justify-center gap-2 border-2 border-white/80 px-6 py-4 font-display text-base font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-red md:text-lg"
                      >
                        <Phone className="h-5 w-5" /> {heroCta.phoneNumber}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {preContent}

        {(() => {
          const outline = keywordOutline ?? (autoHeadings ? [
            { id: "overview", label: autoHeadings.hero },
            { id: "why-us", label: autoHeadings.whyUs },
            ...(gallerySection ? [{ id: "products", label: autoHeadings.products }] : []),
            { id: "applications", label: autoHeadings.serviceAreas },
            ...(faqs && faqs.length > 0 ? [{ id: "faqs", label: autoHeadings.faqs }] : []),
          ] : null);
          return outline && outline.length > 0 ? (
            <PageOutline
              tone="white"
              heading={keywordOutlineHeading ?? `On this page · ${serviceName ?? title}`}
              items={outline}
              imageSrc={keywordOutlineImageSrc}
              imageAlt={keywordOutlineImageAlt}
            />
          ) : null;
        })()}

        {(() => {
          const whyUsNode = (
            <section id="why-us" className="bg-white py-20 scroll-mt-24">
              <div className="mx-auto max-w-[1600px] px-4 lg:px-8">
                <div className="font-display text-sm tracking-[0.3em] text-navy-deep">WHY US</div>
                <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
                  {autoHeadings?.whyUs ?? `Why Choose Texas Made Shade for ${serviceName ?? "Custom Covers"}`}
                </h2>
                <ul className="mt-8 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center bg-navy-deep text-white">
                        <Check className="h-4 w-4" />
                      </div>
                      <span className="text-lg text-navy-deep">{linkifyBrands(f)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          );
          return (
            <>
              {whyUsPlacement === "afterHero" && whyUsNode}
              {whyUsPlacement === "afterHero" && afterWhyUs}
              {gallerySectionPlacement === "beforeLetsTalk" && gallerySectionNode}
              {!hideLetsTalkForm && <LetsTalkForm imageSrc={letsTalkImageSrc} imageAlt={letsTalkImageAlt} extraFields={letsTalkAwningFields ? <AwningExtras defaultSeries={letsTalkAwningSeries} /> : undefined} extraFieldsSummary={letsTalkAwningFields ? AWNING_EXTRA_FIELDS_SUMMARY : undefined} />}
              {whyUsPlacement !== "afterHero" && whyUsNode}
              {whyUsPlacement !== "afterHero" && afterWhyUs}
            </>
          );
        })()}

        <section
          id="details"
          className={`bg-white scroll-mt-24 ${bodyLayout === "accordion" ? "pb-10" : "pb-20"}`}
          aria-labelledby="details-heading"
        >
          <div className="mx-auto max-w-[1600px] px-4 lg:px-8">
            <h2 id="details-heading" className={bodyLayout === "accordion" ? "mx-auto mb-6 max-w-4xl font-display text-2xl font-bold text-navy-deep md:text-3xl" : "sr-only"}>
              {`${serviceName ?? "Service"} — Details & Options`}
            </h2>
            <div className={bodyLayout === "accordion" ? "mx-auto max-w-4xl space-y-3" : "grid gap-6 md:grid-cols-2"}>
              {body.map((b) => {
                const slug = b.heading
                  .toLowerCase()
                  .replace(/&/g, "and")
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/^-+|-+$/g, "");
                const headingId = `${slug}-heading`;
                const panelId = `${slug}-panel`;
                if (b.imageOnly) {
                  return b.image ? (
                    <figure key={b.heading} id={slug} className="scroll-mt-28 max-w-3xl overflow-hidden border-2 border-navy-deep">
                      <LazyImage
                        src={b.image.src}
                        alt={b.image.alt}
                        width={1200}
                        height={900}
                        wrapperClassName="aspect-[4/3] w-full"
                        className="aspect-[4/3] w-full object-cover"
                      />
                    </figure>
                  ) : null;
                }

                const isAccordion = bodyLayout === "accordion";
                const figureNode = b.image ? (
                  <figure className={`overflow-hidden border-2 border-navy-deep ${isAccordion ? "w-full sm:w-56 sm:shrink-0" : "mt-4 max-w-3xl"}`}>
                    <LazyImage
                      src={b.image.src}
                      alt={b.image.alt}
                      width={1200}
                      height={900}
                      wrapperClassName="aspect-[4/3] w-full"
                      className="aspect-[4/3] w-full object-cover"
                    />
                    {b.image.caption && (
                      <figcaption className="border-t-2 border-navy-deep bg-navy-deep px-3 py-1.5 text-[11px] italic text-white/90">
                        {b.image.caption}
                      </figcaption>
                    )}
                  </figure>
                ) : null;

                const textNode = (
                  <div className={isAccordion ? "min-w-0 flex-1" : ""}>
                    {b.text.split(/\n+/).filter(Boolean).map((para, i) => (
                      <p key={i} className={`text-muted-foreground ${i === 0 ? (isAccordion ? "mt-0" : "mt-3") : "mt-2"} ${isAccordion ? "text-sm leading-relaxed" : ""}`}>
                        {linkifyBrands(para)}
                      </p>
                    ))}
                    {b.bullets && b.bullets.length > 0 && (
                      <ul className={`mt-3 list-disc pl-5 text-navy-deep ${isAccordion ? "space-y-1 text-sm" : "space-y-1 text-muted-foreground"}`}>
                        {b.bullets.map((bullet) => (
                          <li key={bullet} className="break-inside-avoid">{linkifyBrands(bullet)}</li>
                        ))}
                      </ul>
                    )}
                    {b.cta && (
                      <Link
                        to={b.cta.href}
                        className={`mt-4 inline-flex items-center gap-2 bg-navy-deep font-display uppercase tracking-wider text-white transition hover:bg-navy-deep/90 ${isAccordion ? "px-4 py-2 text-xs" : "px-5 py-3 text-sm"}`}
                      >
                        {b.cta.label}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    )}
                  </div>
                );

                const inner = isAccordion ? (
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
                    {figureNode}
                    {textNode}
                  </div>
                ) : (
                  <>
                    {figureNode}
                    {textNode}
                  </>
                );

                if (isAccordion) {
                  return (
                    <details
                      key={b.heading}
                      id={slug}
                      className="group scroll-mt-28 border-l-4 border-navy-deep bg-cream shadow-card"
                      aria-labelledby={headingId}
                    >
                      <summary
                        className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 hover:bg-cream/70 sm:px-5 sm:py-4"
                        aria-controls={panelId}
                      >
                        <h3 id={headingId} className="font-display text-base text-navy-deep md:text-lg">
                          {b.heading}
                        </h3>
                        <span aria-hidden="true" className="font-display text-lg text-red transition group-open:rotate-45">
                          +
                        </span>
                      </summary>
                      <div id={panelId} role="region" aria-labelledby={headingId} className="px-4 pb-4 sm:px-5 sm:pb-5">
                        {inner}
                      </div>
                    </details>
                  );
                }

                return (
                  <article key={b.heading} id={slug} className="scroll-mt-28 border-l-4 border-navy-deep bg-cream p-6 shadow-card" aria-labelledby={headingId}>
                    <h3 id={headingId} className="font-display text-xl text-navy-deep">{b.heading}</h3>
                    {inner}
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="applications" className="bg-navy-deep py-10 text-white scroll-mt-24 md:py-12">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="font-display text-xs tracking-[0.3em] text-white">{applicationsEyebrow ?? "APPLICATIONS"}</div>
            <h2 className="mt-1 font-display text-2xl font-bold md:text-3xl">{applicationsHeading ?? autoHeadings?.serviceAreas ?? `Texas Service Areas for ${serviceName ?? "Custom Covers"}`}</h2>
            <div className="mt-5 grid gap-2 sm:grid-cols-2 md:gap-3 lg:grid-cols-4">
              {applications.map((a) => (
                <div key={a} className="border border-white/10 bg-white/5 px-4 py-2.5">
                  <div className="font-display text-sm md:text-base">{a}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {faqs && faqs.length > 0 && (
          <section id="faqs" className="bg-cream py-20 scroll-mt-24">
            <div className="mx-auto max-w-4xl px-4 lg:px-8">
              <details className="group">
                <summary className="flex cursor-pointer list-none items-end justify-between gap-6">
                  <span className="block">
                    <span className="block font-display text-xs uppercase tracking-[0.3em] text-red">★ Common Questions</span>
                    <span className="mt-2 block font-display text-4xl font-bold text-navy-deep md:text-5xl">{autoHeadings?.faqs ?? `${serviceName ?? "Custom Covers"} FAQs`}</span>
                  </span>
                  <span className="font-display text-sm uppercase tracking-[0.2em] text-red">
                    <span className="group-open:hidden">Show all +</span>
                    <span className="hidden group-open:inline">Hide −</span>
                  </span>
                </summary>
                {faqMedia}
                <div className="mt-10 divide-y divide-navy-deep/15 border-y border-navy-deep/15">
                  {faqs.map((f) => (
                    <details key={f.q} className="group/item py-6">
                      <summary className="flex cursor-pointer list-none items-start justify-between gap-6">
                        <h3 className="font-display text-lg font-bold text-navy-deep">{f.q}</h3>
                        <span className="font-display text-2xl leading-none text-red transition group-open/item:rotate-45">+</span>
                      </summary>
                      <div className="mt-4 text-base leading-relaxed text-navy-deep/80">
                        {f.aJsx ?? <p>{linkifyBrands(f.a)}</p>}
                      </div>
                    </details>
                  ))}
                </div>
              </details>
              {!hideFaqJsonLd && (
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                      "@context": "https://schema.org",
                      "@type": "FAQPage",
                      mainEntity: faqs.map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: { "@type": "Answer", text: f.a },
                      })),
                    }),
                  }}
                />
              )}
            </div>
          </section>
        )}

        {!hideRelatedLinks && currentPath && serviceName && (
          <RelatedLinks currentPath={currentPath} serviceName={serviceName} />
        )}

        {!hideAutoInternalLinks && currentPath && serviceName && (() => {
          const slug = currentPath.replace(/^\//, "");
          const hasCityMatrix = SERVICE_CITY_SERVICES.some((s: ServiceCityService) => s.slug === slug);
          return (
            <>
              <ServiceInternalLinks servicePath={currentPath} serviceLabel={serviceName} />
              {hasCityMatrix && <ServiceCityLinks service={slug as ServiceCityService["slug"]} />}
            </>
          );
        })()}


        {cityCallouts && cityCallouts.length > 0 ? (
          <section className="bg-cream py-16">
            <div className="mx-auto max-w-7xl px-4 lg:px-8">
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Service Areas</div>
              <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                {serviceName ? `${serviceName} by City` : "Where We Build"}
              </h2>
              <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {cityCallouts.map((c) => (
                  <Link
                    key={c.to}
                    to={c.to}
                    className="group block border-l-4 border-red bg-white p-6 shadow-card transition hover:shadow-bold"
                  >
                    <div className="inline-flex items-center gap-1.5 font-display text-[11px] uppercase tracking-widest text-red">
                      <MapPin className="h-3.5 w-3.5" /> {c.city}, TX
                    </div>
                    <h3 className="mt-2 font-display text-lg text-navy-deep">{c.anchorText}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{c.blurb}</p>
                    <div className="mt-3 inline-flex items-center gap-2 font-display text-xs uppercase tracking-wider text-navy-deep">
                      Visit {c.city} page <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ) : (
          <section className={`py-12 ${hideRelatedLinks ? "border-t border-navy-deep/10 bg-white" : "bg-cream"}`}>
            <div className="mx-auto max-w-5xl px-4 lg:px-8">
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Texas Service Areas</div>
              <h2 className="mt-2 font-display text-2xl font-bold text-navy-deep md:text-3xl">
                {serviceName ? `${serviceName} Across Texas` : "Serving Texas Cities"}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-navy-deep/85">
                We design, fabricate and install {serviceName?.toLowerCase() ?? "custom shade & cover work"} from our Seguin shop across Central and South Texas — including{" "}
                <Link to="/san-antonio" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">{serviceName ?? "shade work"} in San Antonio</Link>,{" "}
                <Link to="/austin" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">{serviceName ?? "shade work"} in Austin</Link>,{" "}
                <Link to="/new-braunfels" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">{serviceName ?? "shade work"} in New Braunfels</Link>,{" "}
                <Link to="/seguin" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">{serviceName ?? "shade work"} in Seguin</Link>,{" "}
                <Link to="/san-marcos" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">{serviceName ?? "shade work"} in San Marcos</Link>,{" "}
                <Link to="/boerne" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Boerne</Link>,{" "}
                <Link to="/kerrville" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Kerrville</Link>,{" "}
                <Link to="/fredericksburg" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Fredericksburg</Link>,{" "}
                <Link to="/corpus-christi" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Corpus Christi</Link>,{" "}
                <Link to="/rockport" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Rockport</Link>,{" "}
                <Link to="/port-aransas" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Port Aransas</Link>,{" "}
                <Link to="/houston" className="font-semibold text-navy-deep underline decoration-red decoration-2 underline-offset-2 hover:text-red">Houston</Link>, and the broader Hill Country, Gulf Coast and Rio Grande Valley.
              </p>
            </div>
          </section>
        )}

        {gallerySectionPlacement !== "beforeLetsTalk" && gallerySectionNode}

        {gallerySection?.cta && (
          <section className="relative overflow-hidden bg-navy-deep text-white">
            <div className="absolute inset-0 bg-gradient-to-r from-red/30 via-transparent to-red/20" aria-hidden="true" />
            <div className="relative mx-auto flex max-w-7xl flex-col gap-8 px-4 py-16 lg:flex-row lg:items-center lg:justify-between lg:px-8 lg:py-20">
              <div className="max-w-3xl">
                <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Request an Estimate</div>
                <h3 className="mt-3 font-display text-4xl font-bold leading-tight md:text-5xl">{gallerySection.cta.headline}</h3>
                {gallerySection.cta.subhead && (
                  <p className="mt-4 text-lg text-white/85">{gallerySection.cta.subhead}</p>
                )}
                <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80">
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-red" /> Free, no-pressure quote</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-red" /> Usually same-day reply</li>
                  <li className="flex items-center gap-2"><Check className="h-4 w-4 text-red" /> Built in our Seguin shop</li>
                </ul>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap lg:flex-col lg:shrink-0">
                <a
                  href={gallerySection.cta.href}
                  className="inline-flex items-center justify-center gap-2 bg-red px-8 py-5 font-display text-lg font-extrabold uppercase tracking-wider text-white shadow-2xl ring-2 ring-white/30 transition hover:bg-red-deep active:scale-[0.98]"
                >
                  {gallerySection.cta.buttonLabel} <ArrowRight className="h-5 w-5" />
                </a>
                {gallerySection.cta.secondaryButtonLabel && gallerySection.cta.secondaryHref && (
                  <a
                    href={gallerySection.cta.secondaryHref}
                    className="inline-flex items-center justify-center gap-2 bg-white px-7 py-5 font-display text-base font-extrabold uppercase tracking-wider text-navy-deep shadow-xl transition hover:bg-cream active:scale-[0.98]"
                  >
                    {gallerySection.cta.secondaryButtonLabel} <ArrowRight className="h-5 w-5" />
                  </a>
                )}
                {gallerySection.cta.phoneNumber && (
                  <a
                    href={`tel:${gallerySection.cta.phoneNumber.replace(/\D/g, "")}`}
                    className="inline-flex items-center justify-center gap-2 border-2 border-white/80 px-7 py-5 font-display text-base font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
                  >
                    <Phone className="h-5 w-5" /> Call {gallerySection.cta.phoneNumber}
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        {testimonials && testimonials.items.length > 0 && (
          <section className="bg-white py-20">
            <div className="mx-auto max-w-7xl px-4 lg:px-8">
              {testimonials.eyebrow && (
                <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ {testimonials.eyebrow}</div>
              )}
              <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
                {testimonials.title ?? "What Our Customers Say"}
              </h2>
              <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {testimonials.items.map((t, i) => (
                  <figure
                    key={`${t.author}-${i}`}
                    className="relative flex flex-col border-l-4 border-red bg-cream p-6 shadow-card"
                  >
                    <Quote className="absolute right-4 top-4 h-8 w-8 text-red/20" aria-hidden="true" />
                    {typeof t.rating === "number" && (
                      <div role="img" className="flex gap-0.5" aria-label={`${t.rating} out of 5 stars`}>
                        {Array.from({ length: 5 }).map((_, idx) => (
                          <Star
                            key={idx}
                            className={`h-4 w-4 ${idx < t.rating! ? "fill-red text-red" : "text-navy-deep/20"}`}
                            aria-hidden="true"
                          />
                        ))}
                      </div>
                    )}
                    <blockquote className="mt-3 text-base leading-relaxed text-navy-deep">
                      “{t.quote}”
                    </blockquote>
                    <figcaption className="mt-5 border-t border-navy-deep/10 pt-4">
                      <div className="font-display text-sm font-bold uppercase tracking-wider text-navy-deep">
                        {t.author}
                      </div>
                      {(t.role || t.location) && (
                        <div className="mt-1 text-xs text-navy-deep/70">
                          {[t.role, t.location].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>
        )}

        {projectHighlights && projectHighlights.items.length > 0 && (
          <section className="bg-cream py-20">
            <div className="mx-auto max-w-7xl px-4 lg:px-8">
              {projectHighlights.eyebrow && (
                <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ {projectHighlights.eyebrow}</div>
              )}
              <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">{projectHighlights.title}</h2>
              {projectHighlights.intro && (
                <p className="mt-4 max-w-3xl text-lg text-navy-deep/80">{projectHighlights.intro}</p>
              )}
              <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {projectHighlights.items.map((item) => (
                  <article key={item.title} className="flex flex-col border-t-4 border-red bg-white p-6 shadow-card">
                    <h3 className="font-display text-xl font-bold text-navy-deep">{item.title}</h3>
                    <p className="mt-2 text-sm text-navy-deep/80">{item.description}</p>
                    <ul className="mt-4 space-y-2 border-t border-navy-deep/10 pt-4">
                      {item.specs.map((s) => (
                        <li key={s} className="flex items-start gap-2 text-sm text-navy-deep">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-red" aria-hidden="true" />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {children}

        <QuoteCTA />

        {postCta}
      </main>
      <SiteFooter hidePartners={hideFooterPartners} />
    </div>
  );
}
